import { useMemo, useCallback } from "react";
import { IconButton } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { ColDef, GetContextMenuItemsParams, MenuItemDef } from "ag-grid-community";
import { IAGPortfolioDataRow } from "@pulse-ui/types/components/AGTable";

interface UseEntityTableProps {
    onRowExpand: (params: any) => void;
    onEditPortfolio: (id: string, name: string) => void;
    onDeletePortfolio: (id: string, name: string, accountsLength: number) => void;
    onViewAccountDetails: (accountId: string, portfolioId: string | null) => void;
    onDeleteAccount: (accountId: string, name: string, portfolioId: string | null) => void;
    loading: boolean;
}

/**
 * Custom hook for EntityTable configuration
 * Returns AG Grid column definitions, context menus, and detail grid config
 */
export const useEntityTable = ({
    onRowExpand,
    onEditPortfolio,
    onDeletePortfolio,
    onViewAccountDetails,
    onDeleteAccount,
    loading,
}: UseEntityTableProps) => {
    // Master grid column definitions
    const colDefs = useMemo<ColDef<IAGPortfolioDataRow>[]>(
        () => [
            {
                headerName: "Portfolio",
                field: "portfolio_id",
                cellClass: "ag-center-aligned-cell",
                cellRenderer: (params: any) => {
                    return (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                padding: 0,
                                margin: 0,
                            }}
                        >
                            <IconButton
                                disabled={loading}
                                onClick={() => onRowExpand(params)}
                                sx={{
                                    padding: 0,
                                    margin: 0,
                                }}
                            >
                                <ArrowForwardIosIcon
                                    className="pulse-mr-1"
                                    sx={{
                                        fontSize: "0.835rem",
                                        color: "#fff",
                                        transform: `rotate(${
                                            params.node.expanded ? "90deg" : "0deg"
                                        })`,
                                    }}
                                />
                            </IconButton>
                            <p>{params.value}</p>
                        </div>
                    );
                },
            },
            {
                headerName: "Name",
                field: "display_name",
                cellClass: "ag-center-aligned-cell",
            },
            {
                headerName: "Created",
                field: "created",
                cellClass: "ag-center-aligned-cell",
            },
            {
                headerName: "Last Modified",
                field: "last_modified",
                cellClass: "ag-center-aligned-cell",
            },
        ],
        [onRowExpand, loading]
    );

    const defaultColDef: ColDef = {
        flex: 1,
    };

    // Master grid context menu
    const getContextMenuItems = useCallback(
        (params: GetContextMenuItemsParams): MenuItemDef[] => {
            const result: any[] = [
                {
                    name: "Edit Portfolio Name",
                    action: () => {
                        onEditPortfolio(
                            params?.node?.data.portfolio_id,
                            params?.node?.data.display_name
                        );
                    },
                    cssClasses: ["pulse-color--background-50"],
                },
                {
                    name: "Delete Portfolio",
                    action: () => {
                        onDeletePortfolio(
                            params?.node?.data.portfolio_id,
                            params?.node?.data.display_name,
                            params?.node?.data.accounts.length
                        );
                    },
                    cssClasses: ["pulse-color--error-400"],
                },
                ...(params.defaultItems || []),
            ];
            return result;
        },
        [onEditPortfolio, onDeletePortfolio]
    );

    // Detail grid context menu
    const getDetailsContextMenuItems = useCallback(
        (params: GetContextMenuItemsParams): MenuItemDef[] => {
            const result: any[] = [
                {
                    name: "View Details",
                    action: () => {
                        onViewAccountDetails(
                            params?.node?.data.account_id,
                            params?.node?.data.portfolio_id
                        );
                    },
                    cssClasses: ["pulse-color--background-100"],
                },
                {
                    name: "Delete Account",
                    action: () => {
                        onDeleteAccount(
                            params?.node?.data.account_id,
                            params?.node?.data.display_name,
                            params?.node?.data.portfolio_id
                        );
                    },
                    cssClasses: ["pulse-color--error-400"],
                },
                ...(params.defaultItems || []),
            ];
            return result;
        },
        [onViewAccountDetails, onDeleteAccount]
    );

    // Detail grid configuration
    const detailCellRendererParams = useMemo(() => {
        return {
            detailGridOptions: {
                columnDefs: [
                    {
                        headerName: "Account",
                        field: "account_id",
                        cellClass: "ag-center-aligned-cell",
                        autoSize: true,
                        resizable: true,
                        flex: 1,
                    },
                    {
                        headerName: "Name",
                        field: "display_name",
                        cellClass: "ag-center-aligned-cell",
                        autoSize: true,
                        resizable: true,
                        flex: 1,
                    },
                    {
                        headerName: "Created",
                        field: "created",
                        cellClass: "ag-center-aligned-cell",
                        autoSize: true,
                        resizable: true,
                        flex: 1,
                    },
                    {
                        headerName: "Last Modified",
                        field: "last_modified",
                        cellClass: "ag-center-aligned-cell",
                        autoSize: true,
                        resizable: true,
                        flex: 1,
                    },
                ],
                defaultColDef: {
                    sortable: true,
                    filter: true,
                    resizable: true,
                    flex: 1,
                },
                domLayout: "autoSize",
                suppressHorizontalScroll: false,
                getContextMenuItems: getDetailsContextMenuItems,
            },
            getDetailRowData: (params: any) => {
                params.successCallback(params.data.accounts);
            },
        };
    }, [getDetailsContextMenuItems]);

    return {
        colDefs,
        defaultColDef,
        getContextMenuItems,
        detailCellRendererParams,
    };
};
